<template>
  <!--
    Self-contained client bulk-action bar — copied from ClientsView's
    #table-top-actions slot so it can be reused inside the report-task modal.
    Operates on the `selectedIds` prop (client ids) and calls the SAME `clients`
    store actions as the main client table. Emits `applied` on success so the
    parent can reload its list and clear the selection.
  -->
  <b-overlay :show="loading" rounded opacity="0.5" spinner-variant="primary">
    <div class="cba">
      <!-- STEP HEADER -->
      <div class="d-flex align-items-center mb-1">
        <span class="mr-1 small text-muted">{{ $t('operations.bulkActions.selected') || 'Selected' }}: {{ selectedIds.length }}</span>
        <b-badge :variant="currentStep >= 1 ? 'primary' : 'light'" class="mr-1">1. {{ $t('operations.chooseAction') }}</b-badge>
        <b-badge :variant="currentStep >= 2 ? 'primary' : 'light'" class="mr-1">2. {{ $t('tasks.details') || 'Details' }}</b-badge>
        <b-badge :variant="currentStep >= 2 ? 'primary' : 'light'">3. {{ $t('operations.apply') }}</b-badge>
        <b-button size="sm" class="ml-auto" variant="outline-secondary" @click="resetBulkAction">
          <i class="fa-solid fa-rotate-left mr-1"></i>{{ $t('operations.reset') || 'Reset' }}
        </b-button>
      </div>

      <!-- STEP 1: PICK ACTION -->
      <div class="d-flex align-items-center flex-wrap mb-1">
        <b-form-group class="mb-0 mr-1">
          <label class="mb-0 small text-muted">{{ $t('operations.chooseAction') }}</label>
          <b-form-select v-model="bulkAction" :options="bulkActionOptions" class="min-w-220" @change="onPickAction"/>
        </b-form-group>
        <transition name="fade">
          <small v-if="bulkAction" class="text-muted ml-2">{{ actionHint }}</small>
        </transition>
      </div>

      <!-- STEP 2: DETAILS -->
      <transition name="fade">
        <div v-if="currentStep >= 2 && bulkAction !== 'DELETE_COMMENTS'" class="mt-1">
          <div v-if="bulkAction === 'SET_REP'">
            <label class="small text-muted d-block mb-1">{{ $t('operations.bulkActions.setRepresentative') }}</label>
            <user-select-box v-model="optionSelection" :organizations="scopeOrgIds" :business-units="scopeBuIds"/>
          </div>

          <div v-else-if="bulkAction === 'SET_RANDOM_REP'">
            <label class="small text-muted d-block mb-1">{{ $t('operations.bulkActions.setRandomRepresentative') }}</label>
            <user-select-box :multi="true" v-model="optionSelectionMulti" :organizations="scopeOrgIds" :business-units="scopeBuIds"/>
          </div>

          <div v-else-if="bulkAction === 'CHANGE_STATUS'">
            <label class="small text-muted d-block mb-1">{{ $t('operations.bulkActions.setStatus') }}</label>
            <client-status-picker :multiple="false" v-model="optionSelection"/>
          </div>

          <div v-else-if="bulkAction === 'CHANGE_BU'">
            <label class="small text-muted d-block mb-1">{{ $t('operations.bulkActions.changeBusinessUnit') }}</label>
            <business-unit-select-box v-model="optionSelection"/>
          </div>

          <div v-else-if="bulkAction === 'DUPLICATE_TO_ORG'" class="duplicate-to-org">
            <label class="small text-muted d-block mb-1">{{ $t('columns.org') }}</label>
            <organization-select-box v-model="dupOrgId" @input="onDupOrgChange"/>

            <transition name="fade">
              <div v-if="dupOrgId" class="mt-2">
                <label class="small text-muted d-block mb-1">{{ $t('columns.businessUnit') }}</label>
                <v-select v-model="dupBuId" :options="dupBuOptions" :reduce="bu => bu.id" label="name"
                          :clearable="true" @input="onDupBuChange">
                  <template #option="opt">{{ opt.name }} <span class="text-muted small">— {{ opt.type }}</span></template>
                </v-select>
              </div>
            </transition>

            <transition name="fade">
              <div v-if="dupBuId" class="mt-2">
                <label class="small text-muted d-block mb-1">{{ $t('columns.representative') }} <span class="text-muted">({{ $t('operations.optional') || 'optional' }})</span></label>
                <user-select-box v-model="dupRepId" :business-unit-id="dupBuId" :clearable="true"/>
              </div>
            </transition>

            <transition name="fade">
              <div v-if="dupBuId" class="mt-2">
                <label class="small text-muted d-block mb-1">{{ $t('clientDashboard.lastNote') || 'Notes' }}</label>
                <b-button-group size="sm">
                  <b-button :variant="dupCopyNotes === false ? 'danger' : 'outline-danger'" @click="dupCopyNotes = false">
                    <i class="fa-solid fa-trash-can mr-1"></i>{{ $t('clientsBulk.dontCopyNotes') || "Don't copy" }}
                  </b-button>
                  <b-button :variant="dupCopyNotes === true ? 'success' : 'outline-success'" @click="dupCopyNotes = true">
                    <i class="fa-solid fa-clone mr-1"></i>{{ $t('clientsBulk.copyNotes') || 'Copy notes' }}
                  </b-button>
                </b-button-group>
              </div>
            </transition>

            <transition name="fade">
              <div v-if="dupBuId" class="mt-2">
                <label class="small text-muted d-block mb-1">{{ $t('operations.bulkActions.setStatus') }}</label>
                <client-status-picker v-model="dupStatusId" :multiple="false"/>
              </div>
            </transition>
          </div>
        </div>
      </transition>

      <!-- STEP 3: APPLY -->
      <transition name="fade">
        <div v-if="currentStep >= 2" class="mt-2 d-flex align-items-center flex-wrap">
          <b-button size="sm" variant="primary" class="mr-1" :disabled="!isReadyToApply || duplicating || loading" @click="applyBulkAction">
            <b-spinner v-if="duplicating" small class="mr-1"/>
            <i v-else class="fa-solid fa-check mr-1"></i>
            {{ duplicating ? $t('clientsBulk.duplicating', { done: duplicateDone, total: duplicateTotal }) : $t('operations.apply') }}
          </b-button>
          <b-button size="sm" variant="outline-secondary" @click="resetTargets" :disabled="duplicating || loading">
            {{ $t('operations.clear') || 'Clear' }}
          </b-button>
          <div v-if="duplicating && bulkAction === 'DUPLICATE_TO_ORG'" class="ml-2 flex-grow-1" style="min-width: 200px; max-width: 400px;">
            <b-progress :value="duplicateProgress" :max="100" show-progress animated variant="primary" height="14px"/>
          </div>
          <transition name="fade">
            <small v-if="applyError" class="text-danger ml-2">{{ applyError }}</small>
          </transition>
        </div>
      </transition>

      <!-- Duplicate-to-org report modal -->
      <b-modal v-model="showDuplicateReport" :title="$t('clientsBulk.duplicateReport.title')" size="lg"
               ok-only :ok-title="$t('operations.close')" ok-variant="primary" no-close-on-backdrop>
        <div class="mb-3 d-flex align-items-center">
          <b-badge variant="success" class="mr-2">
            <i class="fa-solid fa-check mr-1"></i>{{ $t('clientsBulk.duplicateReport.successCount', { count: duplicateSuccesses.length }) }}
          </b-badge>
          <b-badge variant="danger">
            <i class="fa-solid fa-xmark mr-1"></i>{{ $t('clientsBulk.duplicateReport.failureCount', { count: duplicateFailures.length }) }}
          </b-badge>
        </div>
        <div v-if="duplicateSuccesses.length" class="mb-3">
          <h6 class="text-success mb-1"><i class="fa-solid fa-circle-check mr-1"></i>{{ $t('clientsBulk.duplicateReport.successesHeading') }}</h6>
          <div class="border rounded" style="max-height: 220px; overflow-y: auto;">
            <b-list-group flush>
              <b-list-group-item v-for="(c, i) in duplicateSuccesses" :key="'ds-' + (c.id || i)" class="py-1 small">
                <i class="fa-solid fa-user text-success mr-1"></i><span>{{ c.email || c.id }}</span>
              </b-list-group-item>
            </b-list-group>
          </div>
        </div>
        <div v-if="duplicateFailures.length">
          <h6 class="text-danger mb-1"><i class="fa-solid fa-circle-exclamation mr-1"></i>{{ $t('clientsBulk.duplicateReport.failuresHeading') }}</h6>
          <div class="border rounded" style="max-height: 260px; overflow-y: auto;">
            <b-list-group flush>
              <b-list-group-item v-for="(f, i) in duplicateFailures" :key="'df-' + (f.clientId || i)" class="py-1 small">
                <div class="d-flex justify-content-between">
                  <div><i class="fa-solid fa-user-xmark text-danger mr-1"></i><strong>{{ f.email || f.clientId }}</strong></div>
                  <span class="text-muted">{{ f.clientId }}</span>
                </div>
                <div class="text-danger small mt-1" style="word-break: break-word;">{{ f.reason || $t('clientsBulk.duplicateReport.unknownReason') }}</div>
              </b-list-group-item>
            </b-list-group>
          </div>
        </div>
        <div v-if="!duplicateSuccesses.length && !duplicateFailures.length" class="text-center text-muted py-3">
          {{ $t('clientsBulk.duplicateReport.empty') }}
        </div>
      </b-modal>
    </div>
  </b-overlay>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import mixinBase from '@/components/mixin/mixinBase'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinPermissions from '@/components/mixin/mixinPermissions'
import UserSelectBox from '@/components/widget/select/UserSelectBox.vue'
import BusinessUnitSelectBox from '@/components/widget/select/BusinessUnitSelectBox'
import OrganizationSelectBox from '@/components/widget/select/OrganizationSelectBox.vue'
import ClientStatusPicker from '@/components/widget/picker/clientStatusPicker'
import permissions from '@/model/Permissions'

export default {
  name: 'ClientBulkActions',
  components: { UserSelectBox, BusinessUnitSelectBox, OrganizationSelectBox, ClientStatusPicker },
  mixins: [mixinBase, mixinNotifications, mixinPermissions],
  props: {
    selectedIds: { type: Array, default: () => [] },
    scope: { type: Object, default: () => ({}) },
  },
  data: () => ({
    permissions,
    loading: false,
    bulkAction: null,
    currentStep: 1,
    optionSelection: null,
    optionSelectionMulti: [],
    applyError: null,
    // duplicate-to-org wizard
    dupOrgId: null,
    dupBuId: null,
    dupRepId: null,
    dupStatusId: null,
    dupCopyNotes: false,
    duplicating: false,
    duplicateTotal: 0,
    duplicateDone: 0,
    duplicateSuccesses: [],
    duplicateFailures: [],
    showDuplicateReport: false,
  }),
  computed: {
    ...mapGetters('data', ['allBusinessUnits']),
    ...mapGetters('user', ['isMultiOrgUser']),
    scopeOrgIds() { return this.scope ? this.scope.orgIds : null },
    scopeBuIds() { return this.scope ? this.scope.buIds : null },
    bulkActionOptions() {
      const $this = this
      const options = [{ value: null, text: `— ${$this.$t('operations.bulkActions.selectAction')} —` }]
      if (this.hasPermission(permissions.CLIENT_UPDATE_REPRESENTATIVE)) {
        options.push(
          { value: 'SET_REP', text: $this.$t('operations.bulkActions.setRepresentative') },
          { value: 'SET_RANDOM_REP', text: $this.$t('operations.bulkActions.setRandomRepresentative') },
        )
      }
      options.push({ value: 'CHANGE_STATUS', text: $this.$t('operations.bulkActions.setStatus') })
      if (this.hasPermission(permissions.CLIENT_UPDATE_BU)) {
        options.push({ value: 'CHANGE_BU', text: $this.$t('operations.bulkActions.changeBusinessUnit') })
      }
      if (this.hasPermission(permissions.CLIENT_UPDATE_DETAILS)) {
        options.push({ value: 'DELETE_COMMENTS', text: $this.$t('operations.bulkActions.deleteComments') })
      }
      if (this.isMultiOrgUser) {
        options.push({ value: 'DUPLICATE_TO_ORG', text: $this.$t('operations.bulkActions.duplicateToOrg') })
      }
      return options
    },
    actionHint() {
      switch (this.bulkAction) {
        case 'SET_REP': return this.$t('operations.bulkActions.setRepresentative')
        case 'SET_RANDOM_REP': return this.$t('operations.bulkActions.setRandomRepresentative')
        case 'CHANGE_STATUS': return this.$t('operations.bulkActions.setStatus')
        case 'CHANGE_BU': return this.$t('operations.bulkActions.changeBusinessUnit')
        case 'DUPLICATE_TO_ORG': return this.$t('operations.bulkActions.duplicateToOrg')
        case 'DELETE_COMMENTS': return this.$t('operations.bulkActions.deleteComments')
        default: return ''
      }
    },
    isReadyToApply() {
      if (!this.bulkAction || !this.selectedIds.length) return false
      if (this.bulkAction === 'DELETE_COMMENTS') return true
      if (this.bulkAction === 'SET_RANDOM_REP') return Array.isArray(this.optionSelectionMulti) && this.optionSelectionMulti.length > 0
      if (this.bulkAction === 'DUPLICATE_TO_ORG') return !!this.dupOrgId && !!this.dupBuId
      return !!this.optionSelection
    },
    dupBuOptions() {
      if (!this.dupOrgId) return []
      return (this.allBusinessUnits || []).filter(bu => bu.orgId === this.dupOrgId)
    },
    duplicateProgress() {
      if (!this.duplicateTotal) return 0
      return Math.floor((this.duplicateDone / this.duplicateTotal) * 100)
    },
  },
  methods: {
    ...mapActions('clients', ['updateBusinessUnitOfClient', 'duplicateClientToNewOrgWithOptions', 'deleteCommentsOfClients',
      'updateStatusOfClient', 'updateRepresentativeOfClient', 'updateToRandomRep']),
    onPickAction() {
      this.applyError = null
      this.currentStep = this.bulkAction ? 2 : 1
      this.optionSelection = null
      this.optionSelectionMulti = []
    },
    resetTargets() {
      this.optionSelection = null
      this.optionSelectionMulti = []
      this.applyError = null
      this.dupOrgId = null
      this.dupBuId = null
      this.dupRepId = null
      this.dupStatusId = null
      this.dupCopyNotes = false
    },
    resetBulkAction() {
      this.bulkAction = null
      this.resetTargets()
      this.currentStep = 1
    },
    onDupOrgChange() { this.dupBuId = null; this.dupRepId = null },
    onDupBuChange() { this.dupRepId = null },
    run(promise) {
      this.loading = true
      return Promise.resolve(promise).finally(() => { this.loading = false })
    },
    async applyBulkAction() {
      this.applyError = null
      if (!this.isReadyToApply || this.duplicating || this.loading) return
      try {
        switch (this.bulkAction) {
          case 'SET_REP':
            await this.run(this.updateRepresentativeOfClient({ clientsId: this.selectedIds, newRepresentative: this.optionSelection }))
            break
          case 'SET_RANDOM_REP':
            await this.run(this.updateToRandomRep({ clientsId: this.selectedIds, repIds: this.optionSelectionMulti }))
            break
          case 'CHANGE_STATUS':
            await this.run(this.updateStatusOfClient({ clientsId: this.selectedIds, newStatus: this.optionSelection }))
            break
          case 'CHANGE_BU':
            await this.run(this.updateBusinessUnitOfClient({ clientsId: this.selectedIds, newBusiUnit: this.optionSelection }))
            break
          case 'DELETE_COMMENTS':
            await this.run(this.deleteCommentsOfClients({ clientsId: this.selectedIds }))
            break
          case 'DUPLICATE_TO_ORG':
            await this.duplicateClientToOrg()
            return // duplicate handles its own notifications/report + emits applied
        }
        this.showSuccessNotification(this.$t('globalMessages.updateDataSuccess', { system: this.$t('menu.clients') }))
        this.$emit('applied')
        this.resetBulkAction()
      } catch (e) {
        this.applyError = this.getErrorDescription ? this.getErrorDescription(e) : ((e && e.message) || 'Failed to apply action')
      }
    },
    async duplicateClientToOrg() {
      const clientIds = (this.selectedIds || []).filter(id => id && id !== 'all')
      if (!clientIds.length) return
      this.duplicating = true
      this.duplicateTotal = clientIds.length
      this.duplicateDone = 0
      this.duplicateSuccesses = []
      this.duplicateFailures = []
      this.applyError = null
      const baseReq = {
        orgId: this.dupOrgId,
        buId: this.dupBuId,
        representativeUserId: this.dupRepId,
        copyNotes: this.dupCopyNotes,
        statusId: this.dupStatusId,
      }
      try {
        for (const id of clientIds) {
          try {
            const res = await this.duplicateClientToNewOrgWithOptions({ ...baseReq, clientIds: [id] })
            if (res && Array.isArray(res.successes)) this.duplicateSuccesses.push(...res.successes)
            if (res && Array.isArray(res.failures)) this.duplicateFailures.push(...res.failures)
          } catch (perClientErr) {
            const reason = this.getErrorDescription ? this.getErrorDescription(perClientErr) : ((perClientErr && perClientErr.message) || 'Request failed')
            this.duplicateFailures.push({ clientId: id, email: null, reason })
          } finally {
            this.duplicateDone += 1
          }
        }
        const okCount = this.duplicateSuccesses.length
        const failCount = this.duplicateFailures.length
        if (failCount === 0) {
          this.showSuccessNotification(this.$t('globalMessages.addDataSuccess', { system: this.$t('menu.clients') }))
        } else if (okCount === 0) {
          this.showErrorNotification(this.$t('clientsBulk.duplicateReport.title'), this.$t('clientsBulk.duplicateReport.allFailed', { count: failCount }))
        } else {
          this.showSuccessNotification(this.$t('clientsBulk.duplicateReport.partial', { ok: okCount, failed: failCount }))
        }
      } finally {
        this.duplicating = false
        this.$emit('applied')
        this.resetBulkAction()
        this.showDuplicateReport = true
      }
    },
  },
}
</script>

<style scoped>
.cba { width: 100%; }
.min-w-220 { min-width: 220px; }
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }
</style>
